import type { FieldValidationError, ValidationError } from '@openx/types/api';
import { type HTTPError, isHTTPError } from 'ky';

export interface ApiFieldValidationError<T> {
  fieldId: keyof T;
  error: string;
}

export type ApiFieldValidationErrors<T> = ApiFieldValidationError<T>[];

export interface ApiValidationErrors<T> {
  hasErrors: () => boolean;
  getFieldErrors: () => ApiFieldValidationErrors<T>;
  getOtherErrors: () => string[];
}

function isValidationError<T>(error: unknown): error is ValidationError<T> {
  return ['Not Unique Error', 'Object Validation Error', 'Read Only Error', 'オブジェクトの検証エラー'].includes(
    (error as ValidationError<T>).type
  );
}

function extractErrorResponseData<T>(
  error?: Error | ValidationError<T> | HTTPError | null
): ValidationError<T> | undefined {
  if (isHTTPError(error)) {
    return (Array.isArray(error.data) ? error.data[0] : error.data) as ValidationError<T>;
  }

  if (!error || !isValidationError<T>(error)) {
    return undefined;
  }

  return error;
}

export function apiValidationErrors<T>(
  apiErrorObject?: Error | ValidationError<T> | HTTPError | null
): ApiValidationErrors<T> {
  const responseData = extractErrorResponseData<T>(apiErrorObject);
  const rawFieldsErrors = responseData?.field_errors ? responseData.field_errors : {};

  const fieldErrors = Object.values<FieldValidationError<T>>(rawFieldsErrors).map(error => ({
    error: error.message,
    fieldId: error.attribute,
  }));

  if (responseData?.field) {
    fieldErrors.push({
      error: responseData.message,
      fieldId: responseData.attribute as keyof T,
    });
  }

  const otherErrors = responseData?.message ? [responseData.message] : [];

  return {
    getFieldErrors: () => fieldErrors || [],
    getOtherErrors: () => otherErrors || [],
    hasErrors: () => fieldErrors.length > 0 || otherErrors.length > 0,
  };
}
